import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, Search } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { formatAUD } from "@/lib/media";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function GiftCardBalance() {
  useSeo("Check Gift Card Balance", "Look up the remaining balance on your Happy Bulldog Co. gift card.");
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const check = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/gift-cards/balance", { code: code.trim() });
      setResult(data);
      if (!data.found) toast.error(data.message || "No gift card found.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 bg-[#A78BFA]/15 text-[#19324A] rounded-full px-4 py-1.5 text-sm font-bold"><Wallet className="w-4 h-4 text-[#A78BFA]" /> Gift cards</div>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl text-[#19324A] mt-4">Check your balance</h1>
        <p className="text-[#19324A]/60 mt-3">Enter your gift card code to see what's left to spend.</p>
      </div>

      <form onSubmit={check} className="bg-white rounded-3xl p-6 soft-shadow flex gap-2" data-testid="balance-form">
        <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="HBC-XXXX-XXXX-XXXX"
          className="flex-1 rounded-2xl border-2 border-[#19324A]/10 px-4 py-3 outline-none focus:border-[#A78BFA] uppercase" data-testid="balance-input" />
        <button type="submit" disabled={loading} className="btn-pink !py-3" data-testid="balance-check-btn"><Search className="w-4 h-4" />{loading ? "Checking…" : "Check"}</button>
      </form>

      {result && result.found && (
        <div className="bg-gradient-to-br from-[#A78BFA] to-[#4CC9F0] rounded-3xl p-8 text-white soft-shadow mt-6 text-center" data-testid="balance-result">
          <p className="text-white/80 text-sm">Remaining balance</p>
          <p className="font-display font-extrabold text-5xl mt-1" data-testid="balance-amount">{formatAUD(result.balance)}</p>
          <p className="text-white/80 text-sm mt-3 tracking-widest font-bold">{result.code}</p>
          {result.status === "depleted" && <p className="mt-4 bg-white/15 rounded-full px-4 py-1.5 inline-block text-sm font-semibold">This card has been fully used.</p>}
          {result.status === "expired" && <p className="mt-4 bg-white/15 rounded-full px-4 py-1.5 inline-block text-sm font-semibold">This card has expired.</p>}
          {result.status === "active" && result.expires_at && <p className="text-white/70 text-xs mt-4">Valid until {new Date(result.expires_at).toLocaleDateString("en-AU")}</p>}
        </div>
      )}
      {result && !result.found && (
        <div className="bg-white rounded-3xl p-8 soft-shadow mt-6 text-center text-[#19324A]/60" data-testid="balance-notfound">
          We couldn't find a gift card with that code. Double-check it and try again.
        </div>
      )}

      <p className="text-center text-sm text-[#19324A]/50 mt-8">
        Don't have one yet? <Link to="/gift-cards" className="text-[#A78BFA] font-bold underline">Buy a gift card</Link>
      </p>
    </div>
  );
}
